import {
    ChangeEvent,
    KeyboardEvent,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    ILeoChatFile,
    ILeoChatOption,
} from '../../../../store/chatbox/chatboxApi';
import { useTheme } from '../../../../utils/theme-context';

interface LeoChatInputProps {
    placeholder: string;
    topicId?: string;
    stage?: string;
    options?: ILeoChatOption[];
    disabled?: boolean;
    onClickOption: (option: ILeoChatOption) => void;
    onSend: (message: string, files: ILeoChatFile[], stage?: string) => Promise<void>;
    onUploadFiles: (files: File[], topicId?: string) => Promise<ILeoChatFile[]>;
}

const optionKey = (option: ILeoChatOption) =>
    typeof option === 'string' ? undefined : option?.key;

const optionText = (option: ILeoChatOption) => {
    if (typeof option === 'string') return option;

    return String(
        option?.value
        || option?.label
        || option?.text
        || option?.message
        || option?.title
        || option?.name
        || (typeof option?.key === 'string' ? option.key : ''),
    );
};

const LeoChatInput = ({
    placeholder,
    topicId,
    stage,
    options,
    disabled,
    onClickOption,
    onSend,
    onUploadFiles,
}: LeoChatInputProps) => {
    const { theme } = useTheme();
    const [message, setMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isListening, setIsListening] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const recognitionRef = useRef<any>(null);

    const isDisabled = Boolean(disabled || isUploading);
    const canSubmit = Boolean(message.trim() || selectedFiles.length);
    const sendButtonDisabled = isDisabled || !canSubmit;
    const sendIcon = sendButtonDisabled
        ? (theme === 'dark' ? '/svg/icons/send-white.svg' : '/svg/icons/send.svg')
        : (theme === 'dark' ? '/svg/icons/send.svg' : '/svg/icons/send-white.svg');

    useEffect(() => {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event: any) => {
            const transcript = event.results?.[0]?.[0]?.transcript || '';
            setMessage((prev) => `${prev} ${transcript}`.trim());
        };

        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);
        recognitionRef.current = recognition;
    }, []);

    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = '0px';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 132)}px`;
    }, [message]);

    const handleMicClick = () => {
        if (isDisabled || !recognitionRef.current) return;

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
            return;
        }

        recognitionRef.current.start();
        setIsListening(true);
    };

    const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files) return;

        setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
        event.target.value = '';
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handleSend = async () => {
        if (isDisabled) return;
        const trimmedMessage = message.trim();
        if (!trimmedMessage && selectedFiles.length === 0) return;
        const filesToUpload = [...selectedFiles];

        setMessage('');
        setSelectedFiles([]);
        setIsUploading(true);
        try {
            const uploadedFiles = filesToUpload.length
                ? await onUploadFiles(filesToUpload, topicId)
                : [];

            await onSend(trimmedMessage, uploadedFiles, stage);
        } finally {
            setIsUploading(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== 'Enter') return;
        if (event.shiftKey) return;
        event.preventDefault();
        handleSend();
    };

    return (
        <div className='w-full'>
            {!!options?.length && !isDisabled && (
                <div className='mb-2 flex flex-row flex-nowrap gap-2 overflow-x-auto px-1 pb-1'>
                    {options.map((option, index) => (
                        <button
                            key={`${optionText(option) || optionKey(option) || 'option'}-${index}`}
                            type='button'
                            onClick={() => onClickOption(option)}
                            className='shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-[#252B37] shadow-sm hover:bg-gray-50 dark:border-borderColor-darkMode dark:bg-black dark:text-primary-dark'
                        >
                            {optionKey(option) ? `${optionKey(option)}. ` : ''}
                            {optionText(option)}
                        </button>
                    ))}
                </div>
            )}

            <div
                className={`rounded-lg border bg-white shadow-[0_10px_28px_rgba(15,23,42,0.08)] transition dark:bg-black ${
                    isDisabled
                        ? 'border-gray-200'
                        : 'border-gray-200 focus-within:border-[#00330f] focus-within:shadow-[0_14px_34px_rgba(0,51,15,0.12)] dark:border-gray-600'
                }`}
            >
                {selectedFiles.length > 0 && (
                    <div className='flex flex-wrap gap-2 border-b border-gray-100 px-3 py-2 dark:border-borderColor-darkMode'>
                        {selectedFiles.map((file, index) => (
                            <div
                                key={`${file.name}-${index}`}
                                className='flex max-w-full items-center gap-2 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 dark:bg-white/10 dark:text-primary-dark'
                            >
                                <span className='truncate'>{file.name}</span>
                                <button
                                    type='button'
                                    onClick={() => removeFile(index)}
                                    className='shrink-0 text-gray-500 hover:text-black dark:hover:text-white'
                                    aria-label={`Remove ${file.name}`}
                                >
                                    x
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className='flex items-end gap-2 px-3 py-3'>
                    <button
                        type='button'
                        onClick={handleMicClick}
                        disabled={isDisabled}
                        aria-label={isListening ? 'Stop listening' : 'Start listening'}
                        className={`h-9 w-9 shrink-0 rounded-full border border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-borderColor-darkMode dark:hover:bg-white/10 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                        <img
                            src={theme === 'dark' ? '/svg/icons/microphone-white.svg' : '/svg/icons/microphone.svg'}
                            className={`m-auto h-5 w-5 ${isListening ? 'animate-pulse' : ''}`}
                            alt=''
                        />
                    </button>

                    <textarea
                        ref={textareaRef}
                        value={message}
                        disabled={isDisabled}
                        rows={1}
                        onChange={(event) => setMessage(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isDisabled ? 'Leo is replying...' : placeholder}
                        className='max-h-[132px] min-h-[40px] min-w-0 flex-1 resize-none overflow-y-auto bg-transparent py-2 text-sm leading-6 text-black outline-none placeholder:text-gray-400 disabled:cursor-not-allowed dark:text-white'
                    />

                    <input
                        ref={fileInputRef}
                        type='file'
                        multiple
                        className='hidden'
                        onChange={handleFileUpload}
                        disabled={isDisabled}
                    />

                    <button
                        type='button'
                        disabled={isDisabled}
                        onClick={() => fileInputRef.current?.click()}
                        aria-label='Attach files'
                        className={`h-9 w-9 shrink-0 rounded-full border border-transparent hover:border-gray-200 hover:bg-gray-50 dark:hover:border-borderColor-darkMode dark:hover:bg-white/10 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
                    >
                        <img
                            src={theme === 'dark' ? '/svg/icons/file-upload-white.svg' : '/svg/icons/file-upload.svg'}
                            className='m-auto h-5 w-5'
                            alt=''
                        />
                    </button>

                    <button
                        type='button'
                        disabled={sendButtonDisabled}
                        onClick={handleSend}
                        aria-label='Send message'
                        className={`h-9 w-9 shrink-0 rounded-full transition ${
                            sendButtonDisabled
                                ? 'cursor-not-allowed bg-gray-100 opacity-80 dark:bg-white/10'
                                : 'bg-[#00330f] shadow-sm hover:opacity-90 dark:bg-white'
                        }`}
                    >
                        <img
                            src={sendIcon}
                            className='m-auto h-5 w-5'
                            alt=''
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LeoChatInput;
